import React, { useEffect, useState } from "react";
import { pickColor, initialsOf } from "../../common/UserAvatar/UserAvatar";
import { executeQuery } from "../../../graphql/client";
import { fetchEventImages } from "../../../api/eventImages";
import StoryViewer from "./StoryViewer";
import "./CaptainStoryStrip.css";

// Story strip at the top of UserDashboard's events tab. Two bubbles:
// "Recent photos" (newest event photos) and "Leaders" (all captains
// in one bubble). Both open the fullscreen StoryViewer — tap right to
// advance, left to go back, X to close.

const S3_BASE = "https://s3.amazonaws.com/social-images-public/";

const PUBLIC_CAPTAINS_QUERY = `
  query PublicCaptainsForStrip {
    publicCaptains {
      id
      displayName
      bio
      profileImgKey
      joinDate
      tagline
      eventsHostedCount
    }
  }
`;

const formatTakenAt = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Founder detection is data-driven off the admin-curated tagline so
// it doesn't need a hardcoded user id. Anything with "founder" in the
// tagline gets the founder label; everyone else is a captain.
const roleLabel = (captain) =>
  /founder/i.test(captain.tagline || "")
    ? "Founder / Event Host"
    : "Captain / Event Host";

const photoSlides = (photos) =>
  photos.map((p) => ({
    key: p.s3Key,
    node: (
      <div className="story-slide">
        <img
          className="story-slide__img"
          src={`${S3_BASE}${p.s3Key}`}
          alt={p.displayName || "Recent event photo"}
        />
        {(p.displayName || p.takenAt || p.uploadedAt) && (
          <div className="story-slide__caption">
            {p.displayName && (
              <div className="story-slide__title">{p.displayName}</div>
            )}
            <div className="story-slide__sub">
              {formatTakenAt(p.takenAt || p.uploadedAt)}
            </div>
          </div>
        )}
      </div>
    ),
  }));

const leaderSlides = (captains) =>
  captains.map((c) => {
    const hosted = c.eventsHostedCount || 0;
    return {
      key: c.id,
      node: (
        <div
          className="story-slide"
          style={
            c.profileImgKey
              ? undefined
              : { background: pickColor(c.displayName) }
          }
        >
          {c.profileImgKey ? (
            <img
              className="story-slide__img story-slide__img--cover"
              src={`${S3_BASE}${c.profileImgKey}`}
              alt={c.displayName}
            />
          ) : (
            <span className="story-slide__initials">
              {initialsOf(c.displayName)}
            </span>
          )}
          <div className="story-slide__caption">
            <div className="story-slide__role">{roleLabel(c)}</div>
            <div className="story-slide__title">{c.displayName}</div>
            <div className="story-slide__sub">
              {hosted} event{hosted === 1 ? "" : "s"} hosted
            </div>
            {c.bio && <div className="story-slide__sub">{c.bio}</div>}
          </div>
        </div>
      ),
    };
  });

const CaptainStoryStrip = () => {
  const [captains, setCaptains] = useState([]);
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // "photos" | "leaders" | null
  const [openStory, setOpenStory] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Captains + photos in parallel — both are best-effort, the
      // strip still renders if one fails.
      const [capRes, photoRes] = await Promise.allSettled([
        executeQuery(PUBLIC_CAPTAINS_QUERY, {}),
        fetchEventImages(),
      ]);
      if (cancelled) return;
      if (capRes.status === "fulfilled") {
        setCaptains(capRes.value?.publicCaptains || []);
      }
      if (photoRes.status === "fulfilled") {
        // Already sorted newest-first by the eventImages handler;
        // take the first 5.
        setRecentPhotos((photoRes.value?.items || []).slice(0, 5));
      }
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Don't render the strip until we know whether there's content.
  if (loaded && captains.length === 0 && recentPhotos.length === 0) {
    return null;
  }

  return (
    <>
      <div className="captain-strip" aria-label="Leaders and recent photos">
        <div className="captain-strip__scroller">
          {recentPhotos.length > 0 && (
            <button
              type="button"
              className="captain-strip__item"
              onClick={() => setOpenStory("photos")}
              aria-label="Recent photos"
            >
              <span className="captain-strip__circle captain-strip__circle--photos">
                <span className="captain-strip__photo-collage">
                  {recentPhotos.slice(0, 4).map((p, idx) => (
                    <span
                      key={p.s3Key || idx}
                      className="captain-strip__photo-tile"
                      style={{
                        backgroundImage: `url(${S3_BASE}${p.s3Key})`,
                      }}
                    />
                  ))}
                </span>
              </span>
              <span className="captain-strip__name">Recent photos</span>
            </button>
          )}

          {captains.length > 0 && (
            <button
              type="button"
              className="captain-strip__item"
              onClick={() => setOpenStory("leaders")}
              aria-label="Leaders and captains"
            >
              <span className="captain-strip__circle captain-strip__circle--leaders">
                <span className="captain-strip__photo-collage">
                  {captains.slice(0, 4).map((c) =>
                    c.profileImgKey ? (
                      <span
                        key={c.id}
                        className="captain-strip__photo-tile"
                        style={{
                          backgroundImage: `url(${S3_BASE}${c.profileImgKey})`,
                        }}
                      />
                    ) : (
                      <span
                        key={c.id}
                        className="captain-strip__initial-tile"
                        style={{ background: pickColor(c.displayName) }}
                      >
                        {initialsOf(c.displayName)}
                      </span>
                    )
                  )}
                </span>
              </span>
              <span className="captain-strip__name">Leaders</span>
            </button>
          )}
        </div>
      </div>

      {openStory === "photos" && (
        <StoryViewer
          slides={photoSlides(recentPhotos)}
          onClose={() => setOpenStory(null)}
        />
      )}

      {openStory === "leaders" && (
        <StoryViewer
          slides={leaderSlides(captains)}
          onClose={() => setOpenStory(null)}
        />
      )}
    </>
  );
};

export default CaptainStoryStrip;
